import { useFlags } from 'launchdarkly-react-client-sdk';
import { useTranslation } from 'react-i18next';
import { useSnapshot } from 'valtio';
import { CepFormStepsIndex } from './CepForm.types';
import { StepButtonsConfig } from 'components/ButtonsBar/ButtonsBar';
import { CepStatus, CepSteps, TacticMode } from 'modules/maya/basics/enums/maya.enums';
import { TacticStateType } from 'modules/maya/basics/types/maya.types';
import cepState from 'modules/maya/states/cep.states';
import tacticState from 'modules/maya/states/tactic.states';
import { ButtonType } from 'yoda-ui/Button/Button.types';
import { Icons } from 'yoda-ui/Icons/Icon/Icon';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useCepFormButtons = (yodaFormMethods: any, cepActions: any, isTemplate: boolean) => {
  const {
    stepIndexer,
    saveAsDraft,
    handlePublishEvent,
    isSubmitting,
    saveAndNext,
    saveAndPrevious,
  } = cepActions;
  const { t } = useTranslation();
  const flags = useFlags();
  const { isValid } = yodaFormMethods.useWatchForm();
  const tacticStateValue = useSnapshot(tacticState) as TacticStateType;

  const firstStepButtons: StepButtonsConfig = {
    thirdButton: {
      buttonProps: {
        disabled: !isValid || isSubmitting,
        onClick: saveAndNext,
        buttonType: ButtonType.primary,
      },
      label: t('actions_next_label'),
      loading: isSubmitting,
      hidden: false,
    },
  };

  const middleStepsButtons: StepButtonsConfig = {
    firstButton: {
      buttonProps: {
        disabled: isSubmitting,
        onClick: saveAndPrevious,
        buttonType: ButtonType.secondary,
        startIconName: Icons.chevronLeft,
      },
      label: t('actions_previous_label'),
      hidden: false,
    },
    thirdButton: {
      buttonProps: {
        disabled: isSubmitting,
        onClick: saveAndNext,
        buttonType: ButtonType.primary,
      },
      label: t('actions_next_label'),
      loading: isSubmitting,
      hidden: false,
    },
  };

  let publishLabel = t('maya_action_feedback_cep_button_label');
  switch (cepState?.cep?.status) {
    case CepStatus.FEEDBACK:
      publishLabel = t('maya_action_approve_cep_button_label');
      break;
    case CepStatus.APPROVED:
      publishLabel = t('maya_action_approved_cep_button_label');
      break;
    default:
      publishLabel = t('maya_action_feedback_cep_button_label');
  }

  const lastStepButtons: StepButtonsConfig = {
    firstButton: {
      buttonProps: {
        disabled: false,
        onClick: saveAndPrevious,
        buttonType: ButtonType.secondary,
        startIconName: Icons.chevronLeft,
      },
      label: t('actions_previous_label'),
      hidden: false,
    },
    secondButton: {
      buttonProps: {
        disabled: isSubmitting || cepState?.cep?.status === CepStatus.APPROVED,
        onClick: saveAsDraft,
        buttonType: ButtonType.primary,
      },
      label: t('toast_draft_saved'),
      loading: isSubmitting,
      hidden: false,
    },
    thirdButton: {
      buttonProps: {
        disabled: !isValid || isSubmitting || cepState?.cep?.status === CepStatus.APPROVED,
        onClick: handlePublishEvent,
        buttonType: ButtonType.primary,
      },
      label: publishLabel,
      loading: isSubmitting,
      hidden: isTemplate,
    },
  };

  switch (CepFormStepsIndex[stepIndexer.stepIndex]) {
    case CepSteps.configurationStep:
      return firstStepButtons;
    case CepSteps.targetListStep:
      return middleStepsButtons;
    case CepSteps.tacticsStep:
      return tacticStateValue.mode === TacticMode.form ? null : flags.mayaFinanceBiogenlinc866 ? middleStepsButtons : lastStepButtons;
    case CepSteps.costEstimationStep:
      return lastStepButtons;
    default:
      return null;
  }
};

export default useCepFormButtons;
